import {
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  section: { marginBottom: theme.spacing(4) },
  serviceTitle: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, 2),
    borderRadius: 4,
    marginBottom: theme.spacing(1),
  },
  messageTitle: {
    background: '#e8f5e9',
    padding: theme.spacing(1, 2),
    borderRadius: 4,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  chip: { marginRight: theme.spacing(0.5) },
  code: {
    fontFamily: 'monospace',
    background: '#f5f5f5',
    padding: '2px 6px',
    borderRadius: 3,
    fontSize: '0.85em',
  },
  streamChip: { marginLeft: theme.spacing(1) },
  package: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
}));

interface ProtoField {
  name: string;
  type: string;
  label: string;
  description: string;
}

interface ProtoMethod {
  name: string;
  description: string;
  requestType: string;
  responseType: string;
  requestStreaming: boolean;
  responseStreaming: boolean;
}

interface ProtoService {
  name: string;
  description: string;
  methods: ProtoMethod[];
}

interface ProtoMessage {
  name: string;
  description: string;
  fields: ProtoField[];
}

interface ProtoFile {
  name: string;
  package: string;
  description: string;
  services: ProtoService[];
  messages: ProtoMessage[];
}

interface GrpcDocsData {
  files: ProtoFile[];
}

const streamingLabel = (req: boolean, res: boolean) => {
  if (req && res) return 'Bidirectional';
  if (req) return 'Client Stream';
  if (res) return 'Server Stream';
  return 'Unary';
};

export const GrpcDocsWidget = ({ definition }: { definition: string }) => {
  const classes = useStyles();

  let data: GrpcDocsData;
  try {
    data = JSON.parse(definition);
  } catch {
    return (
      <Box p={2}>
        <Typography color="error">
          Failed to parse gRPC definition JSON.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      {data.files?.map((file: ProtoFile) => (
        <Box key={file.name} className={classes.section}>
          {/* File Header */}
          <Typography variant="h6">{file.name}</Typography>
          {file.package && (
            <Typography variant="body2" className={classes.package}>
              package: <code className={classes.code}>{file.package}</code>
            </Typography>
          )}
          {file.description && (
            <Typography variant="body2" paragraph>
              {file.description}
            </Typography>
          )}

          <Divider />

          {/* Services */}
          {file.services?.map((svc: ProtoService) => (
            <Box key={svc.name} mt={3}>
              <Paper elevation={0} className={classes.serviceTitle}>
                <Typography variant="subtitle1">
                  <strong>service</strong> {svc.name}
                </Typography>
              </Paper>
              {svc.description && (
                <Typography variant="body2" paragraph>
                  {svc.description}
                </Typography>
              )}
              <Table size="small" component={Paper} variant="outlined">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>RPC Method</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Request</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Response</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Pattern</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Description</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {svc.methods?.map((method: ProtoMethod) => (
                    <TableRow key={method.name}>
                      <TableCell>
                        <code className={classes.code}>{method.name}</code>
                      </TableCell>
                      <TableCell>
                        <code className={classes.code}>
                          {method.requestType}
                        </code>
                      </TableCell>
                      <TableCell>
                        <code className={classes.code}>
                          {method.responseType}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={streamingLabel(
                            method.requestStreaming,
                            method.responseStreaming,
                          )}
                          color={
                            method.requestStreaming || method.responseStreaming
                              ? 'secondary'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {method.description}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}

          {/* Messages */}
          {file.messages?.map((msg: ProtoMessage) => (
            <Box key={msg.name} mt={2}>
              <Paper elevation={0} className={classes.messageTitle}>
                <Typography variant="subtitle1">
                  <strong>message</strong> {msg.name}
                </Typography>
              </Paper>
              {msg.description && (
                <Typography variant="body2" paragraph>
                  {msg.description}
                </Typography>
              )}
              {msg.fields?.length > 0 && (
                <Table size="small" component={Paper} variant="outlined">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Field</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Type</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Label</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Description</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {msg.fields.map((field: ProtoField) => (
                      <TableRow key={field.name}>
                        <TableCell>
                          <code className={classes.code}>{field.name}</code>
                        </TableCell>
                        <TableCell>
                          <code className={classes.code}>{field.type}</code>
                        </TableCell>
                        <TableCell>
                          {field.label === 'repeated' && (
                            <Chip size="small" label="repeated" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {field.description}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};
